/**
 * Picking THE class a student row means. Class rows are per branch, per level AND per
 * curriculum: the same name can legitimately exist under two levels ("Cambridge Stage 7-A"
 * under both Cambridge Primary and Cambridge Lower Secondary), under two curricula ("Grade 1-A"
 * under FBISE and Cambridge) and under two branches. A row that carries a branch/level/
 * curriculum (from the template's qualified class label) must land in exactly that class; a
 * plain name is only usable when it is unambiguous. Guessing (first match wins, or the branch's
 * first level) sent students to the wrong level and the server then rejected their subjects —
 * see resolveStudentPlacement.
 */
import {curriculumMatchesHint} from './levelCurriculum'

type Loose = Record<string, any>

const trim = (value: unknown): string => String(value ?? '').trim()
const lower = (value: unknown): string => trim(value).toLowerCase()

/**
 * The students template (GET onboarding/students-template) labels the Class dropdown by:
 *  1. a name unique in the school: bare, "Grade 1-A";
 *  2. shared, but the curriculum tells them apart: "Grade 1-A (FBISE)";
 *  3. shared across levels or branches: "Grade 1-A (Primary - Main Campus)";
 *  4. shared across levels/branches and curricula: "Grade 1-A (Primary, FBISE - Main Campus)";
 *  5. still colliding: that label with " #<classId>" appended.
 * The curriculum part is its short code, else the bracketed part of its name, else the name.
 */
const QUALIFIED_CLASS_LABEL_RE = /^(.*) \((.+) - (.+)\)(?: #\d+)?$/
const CURRICULUM_CLASS_LABEL_RE = /^(.*) \(([^()]+)\)(?: #\d+)?$/

/**
 * Unpacks label forms 2–5 into the class name and the level/branch/curriculum hints; null for
 * a bare name. Callers skip it for a cell that is itself a wizard class name.
 */
export function parseClassLabel(
  label: string
): {className: string; level?: string; branch?: string; curriculum?: string} | null {
  const qualified = label.match(QUALIFIED_CLASS_LABEL_RE)
  if (qualified) {
    const inner = qualified[2].trim()
    // "Primary, FBISE": the part after the last comma is the curriculum.
    const comma = inner.lastIndexOf(', ')
    return {
      className: qualified[1].trim(),
      level: (comma >= 0 ? inner.slice(0, comma) : inner).trim(),
      ...(comma >= 0 ? {curriculum: inner.slice(comma + 2).trim()} : {}),
      branch: qualified[3].trim(),
    }
  }
  const byCurriculum = label.match(CURRICULUM_CLASS_LABEL_RE)
  if (byCurriculum) {
    return {className: byCurriculum[1].trim(), curriculum: byCurriculum[2].trim()}
  }
  return null
}

export type StudentClassMatch =
  | {kind: 'match'; classRecord: Loose}
  | {kind: 'none'}
  | {kind: 'ambiguous'; candidates: Loose[]}

/**
 * Narrows `candidates` (classes already matched by name) by the branch, level and curriculum
 * the row names. A named level must match exactly — a class row with no level never satisfies
 * a row that names one. The curriculum may be written as its display name or short code
 * ("FBISE"; `shortCodes` maps display name → short code). Exactly one survivor is a match;
 * several is ambiguous, never "the first".
 */
export function pickStudentClass(
  candidates: readonly Loose[],
  hints: {branch?: unknown; level?: unknown; curriculum?: unknown},
  shortCodes?: Record<string, string>
): StudentClassMatch {
  const branch = lower(hints.branch)
  const level = lower(hints.level)
  let remaining = candidates
  if (branch) remaining = remaining.filter((c) => lower(c?.branch) === branch)
  if (level) remaining = remaining.filter((c) => lower(c?.level) === level)
  if (trim(hints.curriculum)) {
    remaining = remaining.filter((c) =>
      curriculumMatchesHint(c?.curriculum, hints.curriculum, shortCodes)
    )
  }
  if (remaining.length === 1) return {kind: 'match', classRecord: remaining[0]}
  if (remaining.length === 0) return {kind: 'none'}
  return {kind: 'ambiguous', candidates: [...remaining]}
}

export interface StudentPlacement {
  classRecord?: Loose
  branch: string
  /** Undefined when the row named none and its class could not be pinned to one level. */
  level?: string
  /** The class's curriculum (display name); undefined when it could not be pinned. */
  curriculum?: string
}

/**
 * Branch, level and curriculum to send for a student (or one of their subjects) in the
 * bulk-import payload. The row's own values win; otherwise the class's, but only when the name
 * resolves to exactly one class under those hints. An ambiguous name gets NO level/curriculum,
 * so the server refuses the row with its own clear message instead of the portal guessing.
 *
 * `classes` are the wizard's class rows; names are compared trimmed and case-insensitively.
 */
export function resolveStudentPlacement(
  classes: readonly Loose[],
  row: {class?: unknown; branch?: unknown; level?: unknown; curriculum?: unknown},
  fallbackBranch: string
): StudentPlacement {
  const name = lower(row.class)
  const byName = name ? classes.filter((c) => lower(c?.name) === name) : []
  const match = pickStudentClass(byName, {
    branch: row.branch,
    level: row.level,
    curriculum: row.curriculum,
  })
  const classRecord = match.kind === 'match' ? match.classRecord : undefined
  const level = trim(row.level) || trim(classRecord?.level)
  // The class's own curriculum is the display name to send; the row's may be a short code.
  const curriculum = trim(classRecord?.curriculum) || trim(row.curriculum)
  return {
    classRecord,
    branch: trim(row.branch) || trim(classRecord?.branch) || fallbackBranch,
    ...(level ? {level} : {}),
    ...(curriculum ? {curriculum} : {}),
  }
}

// --- Students template with a separate Curriculum column --------------------------------
//
// Requested with `curriculum_column=true` (GET onboarding/students-template, POST
// data-upload/convert). The `Students` sheet then reads
//   Registration Number | Full Name | Curriculum | Class | Subject 1..N
// where Class is the name WITHOUT its curriculum prefix ("Class 1-A", unique within its
// curriculum only) and Curriculum is the level's stored curriculum name, or the literal
// "No curriculum". A very hidden `Lists` sheet maps every `Curriculum|Class` key back to the
// stored class name, level, branch and curriculum: the values the wizard state and the
// bulk-import payload use. Files downloaded before the change have no Curriculum column and
// keep the label-based path above.

/** Curriculum cell of a class whose level has no curriculum. */
export const NO_CURRICULUM_LABEL = 'No curriculum'

/** Name of the very hidden lookup sheet in the students template. */
export const STUDENTS_TEMPLATE_LISTS_SHEET = 'Lists'

/** Stored values of one class, as listed in the template's `Lists` sheet (columns B-E). */
export interface StudentTemplateClassEntry {
  className: string
  level: string
  branch: string
  /** Blank for a class without a curriculum. */
  curriculum: string
}

/** `Curriculum|Class` lookup key, lower-cased; a blank curriculum reads "No curriculum". */
export const studentTemplateClassKey = (curriculum: unknown, classLabel: unknown): string =>
  `${trim(curriculum) || NO_CURRICULUM_LABEL}|${trim(classLabel)}`.toLowerCase()

/**
 * Index of the `Lists` sheet: one row per class, A `Class` (label), B `Class name`, C `Level`,
 * D `Branch`, E `Curriculum`, F `Key`. Subject lists start at column G and are ignored. The
 * first row is the header. An empty map when the sheet is missing (a CSV, or rows pasted into
 * a new workbook): callers then fall back to matching against the wizard's own classes.
 */
export function buildStudentTemplateListsIndex(
  listsRows: readonly (readonly string[])[] | null | undefined
): Map<string, StudentTemplateClassEntry> {
  const index = new Map<string, StudentTemplateClassEntry>()
  if (!listsRows || listsRows.length < 2) return index
  for (let r = 1; r < listsRows.length; r++) {
    const [label, className, level, branch, curriculum, key] = listsRows[r].map(trim)
    if (!className) continue
    const lookupKey = key ? key.toLowerCase() : studentTemplateClassKey(curriculum, label)
    if (!index.has(lookupKey)) {
      index.set(lookupKey, {className, level, branch, curriculum})
    }
  }
  return index
}

export type CurriculumColumnClassMatch =
  | ({kind: 'match'} & StudentTemplateClassEntry)
  | {kind: 'none'}
  | {kind: 'ambiguous'}

/**
 * The stored class a `Curriculum` + `Class` cell pair of the new layout means.
 *
 * With a `Lists` sheet the pair is a key into it: a hit is exact. Without one, the wizard's own
 * classes are searched: those under the named curriculum ("No curriculum" = none) whose stored
 * name OR display name (`displayName`, the prefix-stripped form) equals the Class cell; a
 * "(Level - Branch)" label narrows by those hints. Exactly one class is a match. Zero or
 * several is reported as such: never a guess.
 *
 * @param displayName Display form of a class name under its curriculum (displayCurriculumName).
 */
export function resolveCurriculumColumnClass(
  cells: {curriculum: unknown; classLabel: unknown},
  listsIndex: ReadonlyMap<string, StudentTemplateClassEntry>,
  wizardClasses: readonly Loose[],
  displayName: (name: unknown, curriculum: unknown) => string,
  shortCodes?: Record<string, string>
): CurriculumColumnClassMatch {
  const classLabel = trim(cells.classLabel)
  if (!classLabel) return {kind: 'none'}

  const listed = listsIndex.get(studentTemplateClassKey(cells.curriculum, classLabel))
  if (listed) return {kind: 'match', ...listed}

  const curriculumCell = trim(cells.curriculum)
  const noCurriculum = !curriculumCell || lower(curriculumCell) === lower(NO_CURRICULUM_LABEL)
  const underCurriculum = wizardClasses.filter((c) =>
    noCurriculum
      ? !trim(c?.curriculum)
      : curriculumMatchesHint(c?.curriculum, curriculumCell, shortCodes)
  )
  const named = (label: string) =>
    underCurriculum.filter(
      (c) =>
        lower(c?.name) === lower(label) ||
        lower(displayName(c?.name, c?.curriculum)) === lower(label)
    )

  // The whole cell first: a class may itself be called "Class 1-A (Evening - Boys)".
  let candidates = named(classLabel)
  if (candidates.length === 0) {
    const qualified = parseClassLabel(classLabel)
    if (qualified) {
      const picked = pickStudentClass(named(qualified.className), {
        level: qualified.level,
        branch: qualified.branch,
      })
      if (picked.kind === 'ambiguous') return {kind: 'ambiguous'}
      candidates = picked.kind === 'match' ? [picked.classRecord] : []
    }
  }

  if (candidates.length === 0) return {kind: 'none'}
  if (candidates.length > 1) return {kind: 'ambiguous'}
  const [match] = candidates
  return {
    kind: 'match',
    className: trim(match?.name),
    level: trim(match?.level),
    branch: trim(match?.branch),
    curriculum: trim(match?.curriculum),
  }
}
